import React from "react";
import { Box, Flex, HStack, Text, Tooltip, Icon } from "@chakra-ui/react";
import { HiOutlineSquares2X2, HiOutlineBookOpen, HiOutlinePaperClip } from "react-icons/hi2";
import { c } from "../classroomColors";
import { brand, onTint } from "../standeTokens";
import { isEmpty } from "./contentTokens";

const Stat = ({ icon, value, label }) => (
  <HStack spacing="7px" align="center">
    <Icon as={icon} boxSize="14px" color={c.textMuted} />
    <Text fontSize="13px" fontWeight="500" color={c.text} sx={{ fontVariantNumeric: "tabular-nums" }}>
      {value}
    </Text>
    <Text fontSize="12.5px" fontWeight="300" color={c.textMuted}>
      {label}
    </Text>
  </HStack>
);

/**
 * How built-out the course is, as one strip of segments — one per lesson, in
 * course order. A filled segment is a written lesson, a hollow one is a gap,
 * and clicking a gap jumps straight to it.
 *
 * This is the teacher's version of a progress bar: not how far a student has
 * got, but how much of the course still needs writing, and exactly where.
 */
const CourseProgress = ({ modules, isInstructor, isDark, onJump }) => {
  if (!modules?.length) return null;

  const b = brand(isDark);
  const tint = onTint(isDark);

  const lessons = modules.flatMap((m) =>
    (m.child_segments || []).map((l) => ({ lesson: l, module: m }))
  );
  const resources = modules.reduce(
    (n, m) =>
      n +
      (m.course_segment_attachments?.length || 0) +
      (m.child_segments || []).reduce((k, l) => k + (l.course_segment_attachments?.length || 0), 0),
    0
  );

  const written = lessons.filter(({ lesson }) => !isEmpty(lesson)).length;
  const pct = lessons.length ? Math.round((written / lessons.length) * 100) : 0;
  const gaps = lessons.length - written;
  const firstGap = lessons.find(({ lesson }) => isEmpty(lesson));

  return (
    <Box
      bg={c.surface}
      border="1px solid"
      borderColor={c.borderSoft}
      borderRadius="14px"
      px="18px"
      py="15px"
      mb="16px"
    >
      <Flex justify="space-between" align="center" gap="14px" wrap="wrap" mb={lessons.length ? "13px" : "0"}>
        <HStack spacing="16px" wrap="wrap" rowGap="6px">
          <Stat icon={HiOutlineSquares2X2} value={modules.length} label={modules.length === 1 ? "module" : "modules"} />
          <Stat icon={HiOutlineBookOpen} value={lessons.length} label={lessons.length === 1 ? "lesson" : "lessons"} />
          {resources > 0 && (
            <Stat icon={HiOutlinePaperClip} value={resources} label={resources === 1 ? "resource" : "resources"} />
          )}
        </HStack>

        {isInstructor && lessons.length > 0 && (
          gaps === 0 ? (
            <Text
              fontSize="11.5px" fontWeight="500" px="9px" py="3px" borderRadius="full"
              bg={b.lightGreen} color={tint.lightGreen}
            >
              Every lesson written
            </Text>
          ) : (
            <Box
              as="button"
              onClick={() => firstGap && onJump(firstGap.lesson)}
              fontSize="11.5px" fontWeight="500" px="9px" py="3px" borderRadius="full"
              bg={b.lightYellow} color={tint.lightYellow}
              _hover={{ opacity: 0.82 }}
              title="Jump to the first lesson that still needs writing"
            >
              {pct}% written · {gaps} to go
            </Box>
          )
        )}
      </Flex>

      {/* one segment per lesson, in course order */}
      {isInstructor && lessons.length > 0 && (
        <Flex gap="3px" align="stretch">
          {lessons.map(({ lesson, module }, i) => {
            const blank = isEmpty(lesson);
            return (
              <Tooltip
                key={lesson.guid}
                label={`${module.name} · ${lesson.name}${blank ? " — needs writing" : ""}`}
                placement="top"
                hasArrow
                fontSize="11px"
                openDelay={200}
              >
                <Box
                  as="button"
                  onClick={() => onJump(lesson)}
                  flex="1"
                  minW="6px"
                  h="7px"
                  borderRadius="3px"
                  bg={blank ? "transparent" : c.text}
                  border={blank ? "1px solid" : "none"}
                  borderColor={b.secondaryOrange}
                  opacity={blank ? 0.85 : 1}
                  transition="transform 0.12s, opacity 0.12s"
                  _hover={{ transform: "scaleY(1.6)", opacity: 1 }}
                  aria-label={`${lesson.name}${blank ? ", needs writing" : ""}`}
                />
              </Tooltip>
            );
          })}
        </Flex>
      )}
    </Box>
  );
};

export default CourseProgress;
